import { useContextStateManagement } from "../../context/ContextApi";
import { useEffect } from "react";
import { useState } from "react";
import { Modal } from "react-bootstrap";
import { Building2, KeyRound, Users, Terminal, Fingerprint, LayoutGrid } from "lucide-react";
import "./ManageOrg.css";
import {
  ORG_PAGE_ACCESS_OPTIONS,
  ORG_PAGE_ACCESS_CATEGORIES,
} from "../../constants/orgPageAccess";

export const ManageOrg = ({
  showLogButton,
  onToggleShowLogs,
  onOpenInboundLogs,
  onOpenOutboundLogs,
}) => {
  const {
    token,
    userType,
    practiceName,
    PracticeId,
    deleteRequest,
    usersList,
    fetchUsersList,
    fetchPracticesList,
    usersPostRequest,
    dateRangeRequest,
    practicesList,
    getOrgPageAccess,
    saveOrgPageAccess,
  } = useContextStateManagement();
  const [loading, setLoading] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [modalType, setModalType] = useState("");
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [rightsLoading, setRightsLoading] = useState(false);
  const [savingRights, setSavingRights] = useState(false);
  const [toolsMessage, setToolsMessage] = useState("");
  const [toolsError, setToolsError] = useState("");
  const [rightsState, setRightsState] = useState({
    inbound: false,
    leadGeneration: false,
    summary: false,
    chatG: false,
  });

  const rightsOptions = [
    { key: "inbound", label: "Inbound Calls", featureId: 1 },
    { key: "leadGeneration", label: "Lead Generation", featureId: 6 },
    { key: "summary", label: "Summary", featureId: null },
    { key: "chatG", label: "Chatbot Lead Generation", featureId: 8 },
  ];

  const [selectedAccessPracticeId, setSelectedAccessPracticeId] = useState("");
  const [selectedAllowedPages, setSelectedAllowedPages] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessMessage, setAccessMessage] = useState("");
  const [accessError, setAccessError] = useState("");

  const chooseActions = () => {
    if (modalType === "delete") {
      handleDelete();
    } else if (modalType === "archive") {
      handleArchive();
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    let obj = { practiceId: PracticeId };
    let url = `/api/removePractice`;

    const userData = await deleteRequest(token, url, obj);
    if (userData === "Token is expired, login again") {
      setIsTokenExpired(true);
      setLoading(false);
    } else if (userData && userData.success === true) {
      setShowSuccessMsg(userData.success);
      fetchPracticesList(userType);
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    setLoading(true);
    let obj = { practiceId: PracticeId };
    let url = `/api/archivePractice`;

    const userData = await deleteRequest(token, url, obj);
    if (userData === "Token is expired, login again") {
      setIsTokenExpired(true);
      setLoading(false);
    } else if (userData && userData.success === true) {
      setShowSuccessMsg(userData.success);
      fetchPracticesList(userType);
      setLoading(false);
    }
  };

  const handleMFARefresh = async (id) => {
    document.querySelector("body").style.cursor = "progress";
    setToolsMessage("");
    setToolsError("");

    const obj = { userId: id };
    const response = await usersPostRequest(token, `/api/MFArefresh`, obj);

    if (response === "Token is expired, login again") {
      setIsTokenExpired(true);
    } else if (response === "MFA refreshed") {
      setToolsMessage("MFA has been disabled successfully.");
      fetchUsersList(PracticeId, token);
    } else if (response === "MFA not refreshed") {
      setToolsError("Failed to disable MFA.");
    } else {
      setToolsError("Something went wrong.");
    }
    document.querySelector("body").style.cursor = "default";
  };

  const fetchRightsState = async (featureId) => {
    const params = featureId == null ? {} : { featureId };
    const userData = await dateRangeRequest(
      token,
      `/api/getAccessRightsDetails/${PracticeId}`,
      params,
    );

    if (userData === "Token is expired, login again") {
      setIsTokenExpired(true);
      return false;
    }

    if (userData === "No record") {
      return false;
    }

    return (
      userData?.RightsEnabled === true ||
      userData?.RightsDetail === "true" ||
      userData?.RightsDetail === true
    );
  };

  const loadAllRights = async () => {
    if (!PracticeId || !token) {
      return;
    }

    setRightsLoading(true);
    const results = await Promise.all(
      rightsOptions.map(async (option) => [
        option.key,
        await fetchRightsState(option.featureId),
      ]),
    );
    setRightsState(Object.fromEntries(results));
    setRightsLoading(false);
  };

  const toggleRightsOption = (key) => {
    setRightsState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveAllRights = async () => {
    setToolsMessage("");
    setToolsError("");
    setSavingRights(true);

    // Each right is saved independently (not Promise.all) so one option that
    // fails to save - e.g. a right that isn't fully configured on the backend
    // yet - doesn't also block the other rights in this same batch from saving.
    const outcomes = await Promise.all(
      rightsOptions.map(async (option) => {
        const result = await usersPostRequest(token, `/api/updateUserRights`, {
          practiceId: PracticeId,
          featureId: option.featureId,
          rightsEnabled: rightsState[option.key],
        });
        return { option, result };
      }),
    );

    if (outcomes.some(({ result }) => result === "Token is expired, login again")) {
      setIsTokenExpired(true);
      setSavingRights(false);
      return;
    }

    const succeeded = outcomes.filter(({ result }) =>
      result === "Rights added successfully" || result === "Rights updated successfully",
    );
    const failed = outcomes.filter(
      ({ result }) =>
        result !== "Rights added successfully" && result !== "Rights updated successfully",
    );

    if (succeeded.length > 0) {
      await loadAllRights();
    }

    if (failed.length === 0) {
      setToolsMessage("Organization rights updated successfully.");
    } else if (succeeded.length === 0) {
      setToolsError("Rights could not be updated. Please try again.");
    } else {
      const failedLabels = failed.map(({ option }) => option.label).join(", ");
      setToolsMessage(
        `${succeeded.map(({ option }) => option.label).join(", ")} updated successfully.`,
      );
      setToolsError(`${failedLabels} could not be saved.`);
    }
    setSavingRights(false);
  };

  useEffect(() => {
    fetchUsersList(PracticeId, token);
  }, [PracticeId, token]);

  useEffect(() => {
    loadAllRights();
  }, [PracticeId, token]);

  useEffect(() => {
    if (userType === "admin" && (!Array.isArray(practicesList) || practicesList.length === 0)) {
      fetchPracticesList(userType);
    }
  }, [practicesList, userType]);

  useEffect(() => {
    if (!PracticeId) {
      return;
    }

    if (!selectedAccessPracticeId) {
      setSelectedAccessPracticeId(String(PracticeId));
    }
  }, [PracticeId]);

  const loadOrgPageAccessForPractice = async (
    practiceId,
    { clearFeedback = true } = {},
  ) => {
    const resolvedPracticeId = Number.parseInt(practiceId, 10);
    if (!resolvedPracticeId) {
      setSelectedAllowedPages([]);
      setAccessLoading(false);
      if (clearFeedback) {
        setAccessError("");
        setAccessMessage("");
      }
      return;
    }

    setAccessLoading(true);
    if (clearFeedback) {
      setAccessError("");
      setAccessMessage("");
    }

    try {
      const response = await getOrgPageAccess(resolvedPracticeId);
      setSelectedAllowedPages(
        Array.isArray(response.allowedPages) ? response.allowedPages : [],
      );
    } catch (error) {
      setSelectedAllowedPages([]);
      setAccessError(error.message || "Unable to load organization page access");
    } finally {
      setAccessLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedAccessPracticeId || !token) {
      return;
    }

    loadOrgPageAccessForPractice(selectedAccessPracticeId);
  }, [selectedAccessPracticeId, token]);

  const organizationOptions = Array.isArray(practicesList) ? practicesList : [];

  const selectedAccessPractice = organizationOptions.find(
    (practice) => Number(practice.Id) === Number(selectedAccessPracticeId),
  );

  const handleTogglePage = (pageId) => {
    setSelectedAllowedPages((currentValue) =>
      currentValue.includes(pageId)
        ? currentValue.filter((entry) => entry !== pageId)
        : [...currentValue, pageId],
    );
    setAccessMessage("");
    setAccessError("");
  };

  const handleOrganizationAccessChange = (event) => {
    const nextPracticeId = event.target.value;
    setSelectedAccessPracticeId(nextPracticeId);
  };

  const handleSavePageAccess = async () => {
    const resolvedPracticeId = Number.parseInt(selectedAccessPracticeId, 10);
    if (!resolvedPracticeId) {
      setAccessError("Select an organization first");
      return;
    }

    setAccessSaving(true);
    setAccessError("");
    setAccessMessage("");

    try {
      const response = await saveOrgPageAccess(
        resolvedPracticeId,
        selectedAllowedPages,
      );
      setSelectedAllowedPages(
        Array.isArray(response.allowedPages) ? response.allowedPages : [],
      );
      setAccessMessage("Organization page access updated successfully.");
    } catch (error) {
      setAccessError(error.message || "Unable to save organization page access");
    } finally {
      setAccessSaving(false);
    }
  };

  if (userType !== "admin") {
    return null;
  }

  const activeRightsCount = Object.values(rightsState).filter(Boolean).length;
  const mfaEnabledCount = usersList.filter((row) => row.MFA_Enabled).length;

  return (
    <div className="manage-org-page">
      <div className="mx-auto manage-org__page-title">
        <span className="manage-org__eyebrow">Admin Center</span>
        <h1>Organization Administration</h1>
      </div>

      <div className="mx-auto manage-org__stat-strip">
        <div className="manage-org__stat-card">
          <span className="manage-org__stat-icon">
            <Users size={16} strokeWidth={2.1} />
          </span>
          <div>
            <strong>{usersList.length}</strong>
            <span>Users</span>
          </div>
        </div>
        <div className="manage-org__stat-card">
          <span className="manage-org__stat-icon">
            <KeyRound size={16} strokeWidth={2.1} />
          </span>
          <div>
            <strong>{`${activeRightsCount} / ${rightsOptions.length}`}</strong>
            <span>Rights Enabled</span>
          </div>
        </div>
        <div className="manage-org__stat-card">
          <span className="manage-org__stat-icon">
            <LayoutGrid size={16} strokeWidth={2.1} />
          </span>
          <div>
            <strong>{`${selectedAllowedPages.length} / ${ORG_PAGE_ACCESS_OPTIONS.length}`}</strong>
            <span>Pages Enabled</span>
          </div>
        </div>
        <div className="manage-org__stat-card">
          <span className="manage-org__stat-icon">
            <Fingerprint size={16} strokeWidth={2.1} />
          </span>
          <div>
            <strong>{mfaEnabledCount}</strong>
            <span>MFA Enabled</span>
          </div>
        </div>
      </div>

      {toolsError ? (
        <div className="mx-auto manage-org__flat">
          <div className="alert alert-danger py-2 mb-0">{toolsError}</div>
        </div>
      ) : null}
      {toolsMessage ? (
        <div className="mx-auto manage-org__flat">
          <div className="alert alert-success py-2 mb-0">{toolsMessage}</div>
        </div>
      ) : null}

      <div className="mx-auto manage-org__flat">
        <div className="manage-org__flat-section">
          <div className="manage-org__section-label">
            <Building2 size={13} strokeWidth={2.2} />
            <span>Organization</span>
          </div>
          <div className="manage-org__flat-row">
            <span className="font-bold manage-org__title">{practiceName}</span>
          </div>
        </div>

        <div className="manage-org__flat-section">
          <div className="manage-org__section-label">
            <KeyRound size={13} strokeWidth={2.2} />
            <span>Access &amp; Rights</span>
          </div>

          <div className="manage-org__flat-list">
            {rightsOptions.map((option) => (
              <label className="manage-org__flat-row manage-org__toggle-row" key={option.key}>
                <input
                  checked={rightsState[option.key]}
                  onChange={() => toggleRightsOption(option.key)}
                  type="checkbox"
                  className="manage-org__checkbox"
                  disabled={rightsLoading || savingRights}
                />
                <span>
                  Enable <strong>{option.label}</strong> Onboarding
                </span>
              </label>
            ))}
          </div>
          <div className="manage-org__flat-footer">
            <button
              type="button"
              className="simple-btn"
              onClick={handleSaveAllRights}
              disabled={rightsLoading || savingRights}
            >
              {savingRights ? "Saving..." : "Save Rights"}
            </button>
          </div>

          <div className="manage-org__access-subsection">
            <div className="manage-org__access-header">
              <div>
                <div className="manage-org__tool-title">Organization Page Access</div>
                <p className="manage-org__access-subtext">
                  Choose which pages are visible for each organization.
                </p>
              </div>
              <div className="manage-org__access-select-wrap">
                <label
                  htmlFor="org-page-access-practice"
                  className="manage-org__access-label"
                >
                  Organization
                </label>
                <select
                  id="org-page-access-practice"
                  className="form-select manage-org__access-select"
                  value={selectedAccessPracticeId}
                  onChange={handleOrganizationAccessChange}
                >
                  <option value="">Select organization</option>
                  {organizationOptions.map((practice) => (
                    <option key={practice.Id} value={practice.Id}>
                      {`${practice.Id} - ${practice.PracticeName || "Unnamed Organization"}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {accessError ? (
              <div className="alert alert-danger py-2 mb-3">{accessError}</div>
            ) : null}
            {accessMessage ? (
              <div className="alert alert-success py-2 mb-3">{accessMessage}</div>
            ) : null}

            {accessLoading ? (
              <div className="d-flex justify-content-center py-4">
                <div className="spinner-border text-secondary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {ORG_PAGE_ACCESS_CATEGORIES.map((category) => {
                  const pagesInCategory = ORG_PAGE_ACCESS_OPTIONS.filter(
                    (page) => page.category === category,
                  );
                  if (pagesInCategory.length === 0) {
                    return null;
                  }
                  return (
                    <div className="manage-org__access-category" key={category}>
                      <div className="manage-org__access-category-label">{category}</div>
                      <div className="manage-org__access-grid">
                        {pagesInCategory.map((page) => (
                          <label key={page.id} className="manage-org__access-option">
                            <input
                              type="checkbox"
                              checked={selectedAllowedPages.includes(page.id)}
                              onChange={() => handleTogglePage(page.id)}
                              disabled={!selectedAccessPracticeId}
                            />
                            <span>{page.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div className="manage-org__access-footer">
                  <div className="manage-org__access-footer-note">
                    {selectedAccessPractice
                      ? `Selected organization: ${selectedAccessPractice.PracticeName || selectedAccessPractice.Id}`
                      : "Select an organization to configure its page access."}
                  </div>
                  <button
                    type="button"
                    className="simple-btn"
                    onClick={handleSavePageAccess}
                    disabled={accessSaving || !selectedAccessPracticeId}
                  >
                    {accessSaving ? "Saving..." : "Save / Set Access"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="manage-org__flat-section">
          <div className="manage-org__section-label">
            <Users size={13} strokeWidth={2.2} />
            <span>Users</span>
          </div>
          <div className="align-center manage-org__table-wrap">
            <table className="manage-org__table">
              <thead className="manage-org__head text-black sticky top-0">
                <tr>
                  <th className="manage-org__heading-cell py-3 font-semibold">
                    Username
                  </th>
                  <th className="manage-org__heading-cell py-3 font-semibold">
                    User Email
                  </th>
                  <th className="manage-org__heading-cell py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {usersList.map((row, index) => (
                  <tr
                    key={index}
                    className={`manage-org__row ${
                      index % 2 === 0 ? "manage-org__row--even" : "manage-org__row--odd"
                    }`}
                  >
                    <td className="manage-org__cell px-6 py-4">
                      {row.UserName}
                    </td>
                    <td className="manage-org__cell px-6 py-4">
                      {row.UserEmail}
                    </td>
                    <td className="manage-org__cell">
                      {row.MFA_Enabled && (
                        <>
                          <button
                            className="simple-btn manage-org__refresh text-center mx-auto d-block"
                            onClick={() => handleMFARefresh(row.UserId)}
                          >
                            MFA Refresh
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="manage-org__flat-section">
          <div className="manage-org__flat-row manage-org__diagnostics-row">
            <div className="manage-org__section-label manage-org__section-label--inline">
              <Terminal size={13} strokeWidth={2.2} />
              <span>Diagnostics</span>
            </div>
            <button
              className="simple-btn manage-org__tool-button"
              onClick={onToggleShowLogs}
            >
              {showLogButton ? "Hide Logs" : "Show Logs"}
            </button>
          </div>

          {showLogButton && (
            <div className="manage-org__log-actions">
              <button
                className="simple-btn manage-org__log-button"
                onClick={onOpenInboundLogs}
              >
                Inbound Logs
              </button>
              <button
                className="simple-btn manage-org__log-button"
                onClick={onOpenOutboundLogs}
              >
                Outbound Logs
              </button>
            </div>
          )}
        </div>

        <div className="manage-org__danger-zone">
          <div className="manage-org__danger-zone-label">Danger Zone</div>
          <div className="manage-org__danger-zone-row">
            <div className="manage-org__danger-zone-copy">
              <strong>{practiceName}</strong>
              <span>
                Archiving hides this organization from active use. Deleting removes it
                permanently — this cannot be undone.
              </span>
            </div>
            <div className="manage-org__danger-zone-actions">
              <button
                className="manage-org__danger-btn"
                onClick={() => {
                  setModalShow(true);
                  setModalType("archive");
                }}
              >
                Archive Practice
              </button>
              <button
                className="manage-org__danger-btn manage-org__danger-btn--delete"
                onClick={() => {
                  setModalShow(true);
                  setModalType("delete");
                }}
              >
                Delete Practice
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        className="popup manage-org__modal"
        show={modalShow}
        onHide={() => setModalShow(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === "delete"
              ? "Delete Organization"
              : "Archive Organization"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="d-flex justify-content-center">
              <div className="spinner-border text-light" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : showSuccessMsg ? (
            <p>
              The selected organization was{" "}
              {modalType === "delete" ? "deleted" : "archived"} successfully.
            </p>
          ) : (
            <p>
              Are you sure you want to{" "}
              {modalType === "delete" ? "permanently delete" : "archive"} this
              organization?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {showSuccessMsg ? (
            <button
              className="simple-btn me-2"
              id="closeTrue"
              onClick={() => {
                setModalShow(false);
                setShowSuccessMsg(false);
              }}
            >
              close
            </button>
          ) : (
            <>
              <button
                className="simple-btn me-2"
                id="closeTrue"
                onClick={() => {
                  setModalShow(false);
                  setShowSuccessMsg(false);
                }}
              >
                No
              </button>
              <button
                className="simple-btn"
                id="updateTrue"
                onClick={chooseActions}
              >
                Yes
              </button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

